import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ADMIN_API } from '../../context/AdminAuthContext';
import { ShoppingBag, Search, ChevronDown, Package, Clock, CheckCircle, Truck, XCircle, RotateCw } from 'lucide-react';

const ORDER_STATUSES = [
  { value: '', label: 'Toutes' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'en_fabrication', label: 'En fabrication' },
  { value: 'expediee', label: 'Expédiée' },
  { value: 'livree', label: 'Livrée' },
  { value: 'annulee', label: 'Annulée' },
];

const STATUS_CONFIG = {
  en_attente: { label: 'En attente', icon: Clock, cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  en_fabrication: { label: 'En fabrication', icon: Package, cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  expediee: { label: 'Expédiée', icon: Truck, cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  livree: { label: 'Livrée', icon: CheckCircle, cls: 'bg-green-500/20 text-green-400 border-green-500/30' },
  annulee: { label: 'Annulée', icon: XCircle, cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const TYPE_LABELS = {
  toile_mesure: 'Toile sur mesure',
  marquage: 'Marquage lambrequin',
  store_complet: 'Store complet',
};

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ADMIN_API}/orders`, {
        params: { status: statusFilter || undefined, limit: 100 },
        withCredentials: true
      });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(p => ({ ...p, [orderId]: true }));
    try {
      const res = await axios.put(`${ADMIN_API}/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: res.data.status } : o));
    } catch (e) { console.error(e); }
    finally { setUpdatingStatus(p => ({ ...p, [orderId]: false })); }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.order_id?.toLowerCase().includes(q) || o.company_name?.toLowerCase().includes(q) || o.user_id?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white">Commandes</h1>
          <p className="text-gray-400 text-sm mt-0.5">{total} commande{total > 1 ? 's' : ''} au total</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
          <RotateCw size={14} /> Actualiser
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par n° commande, société..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 placeholder-gray-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {ORDER_STATUSES.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                statusFilter === opt.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders */}
      {loading ? (
        <div className="space-y-3">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="bg-gray-800 h-24 rounded-xl animate-pulse border border-gray-700" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-12 text-center">
          <ShoppingBag size={40} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Aucune commande trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(order => {
            const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.en_attente;
            const Icon = sc.icon;
            const isExpanded = expandedOrder === order.order_id;
            return (
              <div key={order.order_id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                {/* Main row */}
                <div className="flex items-center justify-between p-4 gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-white font-black font-mono">{order.order_id}</p>
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold border ${sc.cls}`}>
                        <Icon size={12} /> {sc.label}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {order.company_name || 'N/A'} — {order.items?.length} article(s) — {new Date(order.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="text-white font-black">{order.total_ht?.toFixed(2)} € HT</p>

                    {/* Status selector */}
                    <div className="relative">
                      <select
                        value={order.status}
                        onChange={e => updateOrderStatus(order.order_id, e.target.value)}
                        disabled={updatingStatus[order.order_id]}
                        className="bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 cursor-pointer disabled:opacity-50"
                      >
                        {ORDER_STATUSES.filter(s => s.value).map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                      className="p-1.5 text-gray-500 hover:text-white transition-colors"
                    >
                      <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-gray-700 p-4">
                    <p className="text-gray-400 text-xs mb-3">Adresse de livraison : {order.delivery_address || '—'}</p>
                    {order.notes && <p className="text-gray-400 text-xs mb-3">Notes : {order.notes}</p>}
                    <div className="space-y-2">
                      {order.items?.map((item, i) => {
                        const typeLabel = TYPE_LABELS[item.type] || item.type;
                        return (
                          <div key={i} className="bg-gray-700/50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="text-xs bg-gray-600 text-gray-300 px-2 py-0.5 rounded font-semibold">{typeLabel}</span>
                                <p className="text-white font-semibold text-sm mt-1">{item.product_name}</p>
                              </div>
                              <p className="text-orange-400 font-black">{item.total_price?.toFixed(2)} €</p>
                            </div>
                            {item.details && Object.keys(item.details).length > 0 && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                {item.type === 'toile_mesure' && [
                                  ['Largeur', item.details.largeur ? `${item.details.largeur} cm` : null],
                                  ['Avancée', item.details.avancee ? `${item.details.avancee} cm` : null],
                                  ['Lambrequin', item.details.hauteur_lambrequin ? `${item.details.hauteur_lambrequin} cm` : null],
                                  ['Métrage', item.details.metrage_total ? `${item.details.metrage_total} m` : null],
                                  ['Gamme', item.details.gamme || null],
                                  ['Source', item.details.source || null],
                                ].filter(([,v]) => v).map(([k, v]) => (
                                  <div key={k}>
                                    <p className="text-gray-500 text-xs">{k}</p>
                                    <p className="text-gray-200 text-xs font-semibold">{v}</p>
                                  </div>
                                ))}
                                {item.type === 'marquage' && [
                                  ['Texte', item.details.text],
                                  ['Largeur', item.details.largeur ? `${item.details.largeur} cm` : null],
                                  ['Hauteur lambrequin', item.details.hauteur_lambrequin ? `${item.details.hauteur_lambrequin} cm` : null],
                                  ['Toile', item.details.toile || null],
                                ].filter(([,v]) => v).map(([k, v]) => (
                                  <div key={k}>
                                    <p className="text-gray-500 text-xs">{k}</p>
                                    <p className="text-gray-200 text-xs font-semibold">{v}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;

